/**
 * Query key factory for React Query.
 *
 * Use these functions to generate consistent query keys across server prefetch
 * and client-side queries. This prevents SSR hydration mismatches where the
 * server and client use different key structures.
 */

// Skills list params
export interface SkillsListParams {
  page: number;
  limit: number;
  visibility: string;
  isSystemSkill: string;
  search: string;
}

// Agents list params
export interface AgentsListParams {
  page?: number;
  limit?: number;
  search: string;
  /** H2-684 U1: distinguishes the "My Agents" (mine=true) query key from the
   * paginated "Org & Team" query key so the two caches don't collide. */
  mine?: boolean;
}

// Connectors list params
export interface ConnectorsListParams {
  search: string;
  category: string;
  page: number;
  limit: number;
}

// Schedules list params
export interface SchedulesListParams {
  agentId: string;
  page: number;
  limit: number;
}

// PM review list params
export interface PmReviewsListParams {
  page: number;
  limit: number;
  status: string;
  verdict: string;
  workflow: string;
}

// Eval scenario list params (H2-TBD) — mirrors EvalScenariosListParams
// minus undefined noise so keys are stable.
export interface EvalScenariosKeyParams {
  page: number;
  limit: number;
  search: string;
  harness: string;
  status: string;
  source: string;
  type: string;
  tags: string;
  taxonomy: string; // JSON.stringify'd, sorted keys
}

/** H2-1488 — mirrors CuratedDatasetsListParams minus undefined noise, so keys
 *  are stable (same convention as EvalScenariosKeyParams above). */
export interface CuratedDatasetsKeyParams {
  page: number;
  limit: number;
  workflow: string;
  status: string;
}

export interface EvalRunsKeyParams {
  page: number;
  limit: number;
  type: string;
  createdAfter: string;
  createdBefore: string;
}

export const queryKeys = {
  // Skills
  skills: {
    all: ["skills"] as const,
    list: (params: SkillsListParams) => ["skills", params] as const,
    detail: (skillId: string) => ["skill", skillId] as const,
    versions: (skillId: string) => ["skill-versions", skillId] as const,
    files: (skillId: string) => ["skill-files", skillId] as const,
    commits: (skillId: string) => ["skill-commits", skillId] as const,
    commit: (skillId: string, sha: string) =>
      ["skill-commit", skillId, sha] as const,
    compare: (skillId: string, sha: string) =>
      ["skill-compare", skillId, sha] as const,
    branches: (skillId: string) => ["skill-branches", skillId] as const,
    tags: (skillId: string) => ["skill-tags", skillId] as const,
    blame: (skillId: string, filepath: string) =>
      ["skill-blame", skillId, filepath] as const,
    /**
     * H2-1168 — a draft's diff, shared by three surfaces: the Skill Studio draft
     * panel, the approval detail page, and the in-chat propose_skill card. The
     * cache hit between them is the point (a reviewer who sees the diff in chat
     * and then opens the approval shouldn't refetch), and it was previously held
     * together only by the same literal being hand-typed in each place.
     */
    draftDiff: (draftId: string) => ["skill-draft-diff", draftId] as const,
    /** Prefix form, for invalidating every draft's diff at once (draft-view's
     *  afterEdit). Kept next to `draftDiff` so the two cannot drift apart. */
    draftDiffAll: ["skill-draft-diff"] as const,
    // H2-1244 — FDE fork propagation (platform console)
    forks: (skillId: string) => ["skill-forks", skillId] as const,
    /** H2-1416 — fleet-wide fork counts, one entry per forked system skill. */
    forkCounts: ["skill-fork-counts"] as const,
    /** H2-1423 — one propagation target's merged diff (FDE cross-org read). */
    propagationTargetDiff: (propagationId: string, targetId: string) =>
      ["skill-propagation-target-diff", propagationId, targetId] as const,
    propagations: (skillId: string) => ["skill-propagations", skillId] as const,
    /** H2-1551 — every open draft in the active org, classified. One fetch backs
     *  both the Skill Hub row indicators and the skill-detail banner. */
    openDrafts: ["skill-open-drafts"] as const,
  },

  // Agents
  agents: {
    all: ["agents"] as const,
    list: (params: AgentsListParams) => ["agents", params] as const,
    active: ["agents", "active"] as const,
    detail: (agentId: string) => ["agent", agentId] as const,
    skills: (agentId: string) => ["agent-skills", agentId] as const,
    credentials: (agentId: string) => ["agent-credentials", agentId] as const,
  },

  // Connectors
  connectors: {
    all: ["connectors"] as const,
    list: (params: ConnectorsListParams) => ["connectors", params] as const,
    detail: (id: string) => ["connector", id] as const,
  },

  // Channels (H2-622)
  channels: {
    all: ["channels"] as const,
    list: () => ["channels", "list"] as const,
    detail: (integrationId: string) => ["channels", integrationId] as const,
    subscriptions: (integrationId: string) =>
      ["channels", integrationId, "subscriptions"] as const,
    events: (
      integrationId: string,
      filters: Record<string, string | number | undefined>,
    ) => ["channels", integrationId, "events", filters] as const,
    event: (eventId: string) => ["trigger-events", eventId] as const,
    triggers: (integrationId: string) =>
      ["channel-triggers", integrationId] as const,
    subscriptionHealth: (triggerId: string) =>
      ["subscription-health", triggerId] as const,
    scheduledTrigger: (triggerId: string) =>
      ["scheduled-trigger", triggerId] as const,
  },

  // Channel router (H2-1091) — the simplified, association-only channels UI.
  // Distinct root from the legacy `channels` namespace above (D9 collision
  // guard): the two caches must never overlap while both surfaces coexist.
  channelRouter: {
    orgChannels: () => ["channel-router", "org-channels"] as const,
    channelAgents: (channelId: string) =>
      ["channel-router", channelId, "agents"] as const,
  },

  // Integrations catalog (H2-622)
  integrations: {
    list: () => ["integrations"] as const,
  },

  // Schedules
  schedules: {
    all: ["schedules"] as const,
    list: (params: SchedulesListParams) => ["schedules", params] as const,
    byAgent: (agentId: string) => ["schedules", agentId] as const,
  },

  // Sessions
  sessions: {
    all: ["sessions"] as const,
    detail: (sessionId: string) => ["session", sessionId] as const,
    messages: (sessionId: string) => ["session-messages", sessionId] as const,
    tasks: (sessionId: string) => ["session-tasks", sessionId] as const,
    byAgent: (agentId: string, page: number, limit: number) =>
      ["sessions", agentId, page, limit] as const,
  },

  // Files
  files: {
    byAgent: (agentId: string) => ["files", "agent", agentId] as const,
    bySession: (sessionId: string) => ["files", "session", sessionId] as const,
  },

  // Credentials
  credentials: {
    all: ["credentials"] as const,
    detail: (credentialId: string) => ["credentials", credentialId] as const,
    agents: (credentialId: string) =>
      ["credentials", credentialId, "agents"] as const,
    byIntegration: (integrationId: string) =>
      ["credentials", { integrationId }] as const,
  },

  // Analytics
  analytics: {
    throughput: (range: string) => ["analytics", "throughput", range] as const,
    savings: (range: string) => ["analytics", "savings", range] as const,
    credits: (range: string, associationId: string | null) =>
      ["analytics", "credits", range, associationId] as const,
  },

  // Home dashboard
  home: {
    agents: ["home", "agents"] as const,
    agentTasks: (agentId: string, range: string) =>
      ["home", "agent-tasks", agentId, range] as const,
  },

  // HITL approvals
  hitl: {
    pending: (sessionId: string) => ["hitl-pending", sessionId] as const,
    myPending: ["hitl-my-pending"] as const,
  },

  // Escalations (H2-770) — audience-scoped resolver list. Distinct from the
  // legacy hitl-my-pending key (different row shape) so the two caches don't
  // collide while the legacy badge path is still live (retired in Task 14).
  escalations: {
    mine: ["escalations-mine"] as const,
    mineByStatus: (statuses: readonly string[]) =>
      ["escalations-mine", { statuses }] as const,
  },

  // PM reviews
  pmReviews: {
    all: ["pm-reviews"] as const,
    workflows: ["pm-review-workflows"] as const,
    list: (params: PmReviewsListParams) => ["pm-reviews", params] as const,
    detail: (id: string) => ["pm-review", id] as const,
  },

  // Evals (out-of-OpenAPI; H2-TBD scenario management)
  evals: {
    scenarios: {
      all: ["eval-scenarios"] as const,
      list: (params: EvalScenariosKeyParams) =>
        ["eval-scenarios", params] as const,
      detail: (id: string) => ["eval-scenario", id] as const,
    },
    runs: {
      all: ["eval-runs"] as const,
      list: (params: EvalRunsKeyParams) => ["eval-runs", params] as const,
      detail: (id: string) => ["eval-run", id] as const,
      instances: (runId: string) => ["eval-run-instances", runId] as const,
      instance: (runId: string, instanceId: string) =>
        ["eval-run-instance", runId, instanceId] as const,
      adjudication: (runId: string, instanceId: string) =>
        ["eval-run-instance-adjudication", runId, instanceId] as const,
    },
    types: ["eval-scenario-types"] as const,
    taxonomy: (typeId: string) => ["eval-taxonomy", typeId] as const,
    fixtureContent: (scenarioId: string, fixtureId: string) =>
      ["eval-fixture-content", scenarioId, fixtureId] as const,
    sweeps: {
      all: ["eval-sweeps"] as const,
      matrix: (orgId?: string) => ["eval-sweep-matrix", orgId] as const,
      detail: (id: string) => ["eval-sweep", id] as const,
      report: (id: string) => ["eval-sweep-report", id] as const,
    },
    mcConfidence: {
      all: () => ["eval-mc-confidence"] as const,
    },
    // H2-1488 — LoRA training datasets (the corpus, not the benchmark).
    datasets: {
      all: ["curated-datasets"] as const,
      list: (params: CuratedDatasetsKeyParams) =>
        ["curated-datasets", params] as const,
      detail: (id: string) => ["curated-dataset", id] as const,
    },
  },

  // Workflows (Task Automation — the /tasks control plane, surfaced as
  // "Workflows" in the UI). Grouped list + per-workflow detail/runs.
  workflows: {
    all: ["workflows"] as const,
    list: (groupBy?: string) =>
      ["workflows", { groupBy: groupBy ?? null }] as const,
    detail: (id: string) => ["workflow", id] as const,
    runs: (id: string) => ["workflow-runs", id] as const,
    // H2-2148 — the cheap counts over task_runs. Under `workflows.all` because
    // a workflow mutation legitimately changes them.
    health: (id: string) => ["workflows", "health", id] as const,
    // H2-2148 — the LIVE backlog probe. Its own key, deliberately outside
    // `workflows.all`: it costs a Vantaca pull plus v1's find-matches, so an
    // unrelated workflow edit must never invalidate it into a refetch. The
    // operator refetches it explicitly.
    backlog: (id: string) => ["workflow-backlog", id] as const,
    // H2-1941 — the org-wide Runs feed (pinned-awaiting + recency) and the
    // composite Run Detail. The feed key is scoped by kind + the light filters
    // so a filter change is a distinct cache entry (keyset-stable pagination).
    runsFeed: (
      kind: "awaiting" | "recency",
      filters: { agentId: string; taskId: string; status: string },
    ) => ["workflow-runs-feed", kind, filters] as const,
    runDetail: (runId: string) => ["workflow-run-detail", runId] as const,
    // H2-2098 — v1 linkage for one workflow. Its own key (not under
    // `workflows.all`) because Fleet stores NO linkage state: this is a live
    // lookup against v1, so a workflow PATCH does not invalidate it and an
    // unrelated workflow edit must not refetch v1.
    v1Link: (id: string) => ["workflow-v1-link", id] as const,
    // H2-2098 — the same live v1 lookup for a workflow that does not exist yet,
    // keyed by the PREDICATE the create form has instead of by an id. The
    // predicate is in the key so an answer for an older selection can never be
    // rendered against a newer one.
    v1Preview: (actionTypeId: number, entryStepIds: readonly number[]) =>
      ["workflow-v1-link-preview", actionTypeId, [...entryStepIds]] as const,
  },

  // Models
  models: ["models"] as const,

  // User preferences
  userPreferences: ["user-preferences"] as const,
};
